/**
 * ============================================================
 * assets/eh-product-grid.js  — v6.12
 * ============================================================
 * Global product card enhancement script.
 * Processes .eh-variant-summary elements on page load AND
 * when AJAX-injected (product recommendations, recently viewed, etc.)
 *
 * v6.6 fix: Swym wishlist buttons on product cards lost their click
 * handlers because cloneNode(true) strips all JS event listeners.
 * We mark cloned image containers with data-eh-cloned and re-implement
 * wishlist actions via delegated click + Swym JS API so buttons work
 * on both static and AJAX-injected cards without touching non-cloned ones.
 *
 * Installation: Add to theme.liquid before </body>:
 *   <script src="{{ 'eh-product-grid.js' | asset_url }}" defer></script>
 * ============================================================
 */
(function () {
  'use strict';

  /* ── Scroll-jank gate for the hover flip (added 2026-09-14) ──
   * When the cursor rests over a rail and the page scrolls, every card that passes under the
   * STATIONARY pointer fires mouseenter then mouseleave, and showImg() writes four inline styles
   * mid-scroll. The next script to read layout in that frame pays 33 to 56 ms of forced style and
   * layout. Measured 2026-09-14: 4 to 6 dropped frames per rail pass, longest frame 50 to 100 ms.
   * A deliberate hover still flips instantly, because the pointer has moved.
   * Module scope, registered ONCE, not per card.
   * ONLY mouseenter is gated. mouseleave is deliberately NOT gated: with mouseenter gated a
   * scrolled-past card never reaches currentIdx 1, so its mouseleave already early-returns from
   * showImg() with zero style writes. Gating mouseleave as well bought nothing and left a
   * deliberately hovered card stuck on its hover photo after you scrolled away. Measured, 2026-09-14.
   * SETTLE QUEUE (added 2026-09-14, late). Suppressing the flip is not enough on its own. If the
   * scroll STOPS with a card under a resting cursor, the pointer is already inside that card, so
   * mouseenter never fires again and the card would sit on image 1 until the mouse moved. MAIN
   * flips it. Measured 3 of 3 both ways: FINDINGS-hover-gate-settle-gap-2026-09-14.md.
   * So a suppressed mouseenter parks ONE closure in _ehPend, and 120 ms after the last scroll
   * event it re-reads :hover and flips if the cursor is still there. One slot is correct, because
   * only the last card the pointer touched can still be under it when the scroll settles, and
   * :hover is re-read at apply time so the answer is the truth and not a stale guess.
   * It cannot bring the jank back: the write lands after 120 ms of scroll silence, never in a
   * frame another script is about to read layout in, which is the whole cause.
   * REVERT: delete the `if (!ehHoverIsReal()) { ... return; }` guard on the mouseenter listener
   * below. This block can then stay, it is inert.
   * Record: FINDINGS-rail-scroll-jank-2026-09-14.md + FINDINGS-hover-gate-settle-gap-2026-09-14.md
   */
  var _ehLastMove = 0, _ehLastScroll = 0, _ehPend = null, _ehSettleT = null, _ehScrollCls = false, _ehClsT = null;
  /* INT 2026-09-17 v2: mirror this hover gate for CSS-only cards (Searchanise collection and search
     grids, which carry no .eh-variant-summary so nothing below ever runs on them). A class on <html>
     while the page scrolls; the stylesheet suppresses :hover flips under it.
     v1 cleared the class on the 120 ms settle timer and it FLAPPED: any gap between scroll events
     longer than 120 ms removed and re-added the class, and every toggle invalidates style for the
     WHOLE document. Measured cost of v1 vs ungated: dropped frames 2/0/0 to 3/1/2, longest frame
     16.8 to 49.9 ms, and 7 to 10 frames still flipped.
     v2 gives the class its OWN longer timer so it cannot flap inside a gesture, and any deliberate
     pointer movement clears it at once. The 120 ms PDP settle queue is untouched. */
  function _ehScrollOn(){ if(!_ehScrollCls){ _ehScrollCls = true; document.documentElement.classList.add('int-scrolling'); } }
  function _ehScrollOff(){ if(_ehScrollCls){ _ehScrollCls = false; document.documentElement.classList.remove('int-scrolling'); } }
  document.addEventListener('mousemove', function () {
    _ehLastMove = performance.now();
    _ehScrollOff();
  }, { passive: true });
  window.addEventListener('scroll', function () {
    _ehLastScroll = performance.now();
    _ehScrollOn();
    clearTimeout(_ehClsT);
    _ehClsT = setTimeout(_ehScrollOff, 250);
    clearTimeout(_ehSettleT);
    _ehSettleT = setTimeout(function () {
      var fn = _ehPend; _ehPend = null; if (fn) fn();
    }, 120);
  }, { passive: true });
  function ehHoverIsReal() {
    var t = performance.now();
    return !(t - _ehLastScroll < 150 && t - _ehLastMove > 150);
  }

  /* ── Process a single .eh-variant-summary element ── */
  function processCard(thisSum) {
    /* Skip if already processed */
    if (thisSum.dataset.ehProcessed) return;
    thisSum.dataset.ehProcessed = '1';

    var card = thisSum.closest('.product-grid-item') || thisSum.closest('.grid-product');
    if (!card) return;

    /* ── Detect carousel context (product page recommendations) ── */
    var isCarousel = !!(
      card.closest('.product-recommendations') ||
      card.closest('[data-section-type="product-recommendations"]') ||
      card.closest('[data-section-type="related-products"]') ||
      /* ⚑ 2026-09-17: THE PDP RAILS ARE "related", NOT "related-products", and without this
         line every selector above evaluated FALSE at phone width, so `if (!isCarousel)` passed
         and the three touch listeners were attached to every rail card. Flickity switches off
         below 768, so `.flickity-slider` could not catch them either. Result: an invisible
         big-image flip swipe competing with the swipe of the rail itself. Proven with a real
         CDP touch, not a synthetic TouchEvent, which does not fire it and gives a false pass.
         `isCarousel` is read in exactly one other place, `if (!isCarousel)` below, which gates
         only those touch listeners, so this line changes the swipe and nothing else.
         Collection and homepage grids are not `data-section-type="related"` and keep both their
         swipe and their dots. REVERT: delete this one selector. */
      card.closest('[data-section-type="related"]') ||
      card.closest('[data-section-type="recently-viewed"]') ||
      card.closest('.flickity-slider') ||
      card.closest('.swiper-wrapper') ||
      card.closest('.shopify-section--product') ||
      card.closest('.product-page')
    );

    /* ── v6.12, 2026-09-23, Nils: NO SECOND IMAGE ON A PHONE IN THE PDP RAILS ──
       On a touch screen a tap fires an emulated mouseenter, so the hover flip below showed the
       second image on every tap in the two PDP rails. The small colour thumbnails already carry
       the other images there. So on a device that cannot hover, in a carousel context, the card
       keeps its first image only: no flip, no second image download, no flip dots.
       Desktop is unchanged: a mouse still gets the hover image.
       Collection and homepage grids are not carousels and keep their swipe and dots.
       REVERT: delete this block and the two `noFlip` guards below. */
    var noFlip = isCarousel && !(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

    /* ── Hide Quick Add (always) ── */
    ['quick-add-product', '[data-quick-add-holder]', 'form[action="/cart/add"]',
      'div[id^="quick-add-menu-toolbar"]', '.product-grid-item__overlay',
      '.btn--quick-add', '.quick-add-btn'].forEach(function (sel) {
      card.querySelectorAll(sel).forEach(function (el) { el.style.display = 'none'; });
    });

    /* ── Mobile subtitle 2-line breaks (always) ── */
    if (window.innerWidth < 750) {
      var subtitle = card.querySelector('.product__grid__subtitle');
      if (subtitle) {
        var raw = subtitle.textContent.trim();
        var norm = raw.replace(/[\u2019\u2018\u00b4\u0060]/g, "'");
        var breakRules = [
          ["Premium Wood Frame with Matte Paper", "Premium Wood Frame"],
          ["Wood Frame with Matte Paper", "Wood Frame"],
          ["Organic Unisex Crewneck Sweatshirt", "Organic Unisex"],
          ["Organic Unisex Long Sleeve T-shirt", "Organic Unisex"],
          ["Recycled Blend Unisex Oversized Hoodie", "Recycled Blend"],
          ["Recycled Blend Unisex Hoodie", "Recycled Blend"],
          ["Organic Women's Oversized T-shirt", "Organic Women's"],
          ["Organic Men's Oversized T-shirt", "Organic Men's"],
          ["Organic Men's Fitted T-shirt", "Organic Men's"],
          ["Organic Unisex Hoodie", "Organic"],
          ["Organic Unisex T-shirt", "Organic"],
          ["Apple Tough Case", "Apple"],
          ["Samsung Slim Case", "Samsung"],
          ["Apple Slim Case", "Apple"],
          ["Organic Tote Bag", "Organic"],
          ["Organic One Size", "Organic"]
        ];
        for (var r = 0; r < breakRules.length; r++) {
          if (norm === breakRules[r][0]) {
            var bp = breakRules[r][1].length;
            subtitle.innerHTML = raw.substring(0, bp) + '<br>' + raw.substring(bp).trim();
            break;
          }
        }
      }
    }

    /* ══════════════════════════════════════════════════════════════
       FLIP + DOTS — enabled everywhere
       Mobile swipe only on non-carousel grids
       ══════════════════════════════════════════════════════════════ */

    var flipSrc = thisSum.dataset.ehFlipSrc;
    if (!flipSrc) return;

    /* ── Mark card for CSS ── */
    card.classList.add('eh-flip-card');

    /* ── REMOVE Pipeline's extra image wrappers from DOM entirely ──
       Pipeline's JS can't re-show elements that don't exist anymore.
       We keep only the first <product-grid-item-image>. */
    var nativeWrappers = card.querySelectorAll('product-grid-item-image');
    for (var w = nativeWrappers.length - 1; w >= 1; w--) {
      nativeWrappers[w].parentNode.removeChild(nativeWrappers[w]);
    }

    /* Also remove any Pipeline native flip image classes */
    ['.product__grid__image__second', '.product__grid__toggle__image', '[data-grid-image-toggle]'].forEach(function (sel) {
      card.querySelectorAll(sel).forEach(function (el) { el.parentNode.removeChild(el); });
    });

    /* Remove Pipeline hover attributes from images container */
    var pipelineImagesEl = card.querySelector('.product-grid-item__images');
    if (pipelineImagesEl) {
      pipelineImagesEl.removeAttribute('data-grid-slide');
      pipelineImagesEl.removeAttribute('data-hover-image');
    }

    /* ── Hide Pipeline native dots ── */
    card.querySelectorAll('.pgi-img-dots').forEach(function (el) { el.style.display = 'none'; });

    /* ── Our dots ── */
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'eh-img-dots';
    dotsWrap.style.cssText = 'display:flex;justify-content:center;gap:6px;padding:6px 0 2px;';
    var dot0 = document.createElement('span');
    var dot1 = document.createElement('span');
    var dotBase = 'display:inline-block;width:6px;height:6px;border-radius:50%;transition:background 0.15s;';
    dot0.className = 'eh-dot';
    dot1.className = 'eh-dot';
    dot0.style.cssText = dotBase + 'background:#00FFBF;';
    dot1.style.cssText = dotBase + 'background:#ccc;';
    dotsWrap.appendChild(dot0);
    dotsWrap.appendChild(dot1);

    var imagesLink = card.querySelector('a.product-grid-item__images')
      || card.querySelector('.product-grid-item__images')
      || card.querySelector('a[href*="/products/"]');

    /* ── Clone the images container to strip Pipeline's hover listeners ──
       Pipeline's ProductGridItemVariant.mouseEnterEvent causes visual glitches
       and errors ("No child element at index 1") because it still tries to
       access removed image wrappers. Cloning strips all JS listeners.
       NOTE: This also strips Swym's wishlist click handler — the delegated
       Swym handler at the bottom of this file re-implements it by detecting
       the data-eh-cloned attribute we set here. */
    if (imagesLink && imagesLink.parentNode) {
      var cloned = imagesLink.cloneNode(true);
      imagesLink.parentNode.replaceChild(cloned, imagesLink);
      imagesLink = cloned;

      /* ── v6.6: Mark as cloned so delegated Swym handler knows to intercept ── */
      imagesLink.dataset.ehCloned = '1';

      if (!noFlip) imagesLink.parentNode.insertBefore(dotsWrap, imagesLink.nextSibling);
    }

    /* ── Create overlay on images container ── */
    if (!imagesLink) return;
    if (noFlip) return;
    imagesLink.style.position = 'relative';
    imagesLink.style.display = 'block';
    imagesLink.style.overflow = 'hidden';

    var overlay = new Image();
    overlay.src = flipSrc;
    var flipSrcset = thisSum.dataset.ehFlipSrcset || '';
    if (flipSrcset) overlay.srcset = flipSrcset;
    overlay.sizes = '(max-width: 749px) 50vw, 33vw';
    overlay.alt = '';
    overlay.loading = 'eager';
    overlay.decoding = 'async';
    overlay.className = 'eh-flip-overlay';
    overlay.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;opacity:0;transition:opacity 0.15s ease;pointer-events:none;z-index:3;';

    imagesLink.appendChild(overlay);
    if (overlay.decode) overlay.decode().catch(function () { });

    /* ── MutationObserver: if Pipeline ever re-inserts image wrappers, kill them ── */
    if (typeof MutationObserver !== 'undefined') {
      var cardObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeName && node.nodeName.toLowerCase() === 'product-grid-item-image') {
              var allWraps = card.querySelectorAll('product-grid-item-image');
              for (var i = allWraps.length - 1; i >= 1; i--) {
                allWraps[i].parentNode.removeChild(allWraps[i]);
              }
            }
          });
        });
      });
      cardObserver.observe(imagesLink, { childList: true });
    }

    var currentIdx = 0;

    /* Get reference to the main product image to fade it out during flip */
    var mainImg = imagesLink.querySelector('img:not(.eh-flip-overlay)');
    if (mainImg) mainImg.style.transition = 'opacity 0.15s ease';

    function showImg(idx) {
      if (idx === currentIdx) return;
      currentIdx = idx;
      overlay.style.opacity = idx === 1 ? '1' : '0';
      /* Fade main image out when overlay shows, back in when it hides */
      if (mainImg) mainImg.style.opacity = idx === 1 ? '0' : '1';
      dot0.style.background = idx === 0 ? '#00FFBF' : '#ccc';
      dot1.style.background = idx === 1 ? '#00FFBF' : '#ccc';
    }

    /* ── Desktop: hover flip (everywhere) ── */
    imagesLink.addEventListener('mouseenter', function () {
      if (!ehHoverIsReal()) {
        /* Suppressed mid-scroll. Park this card and re-test :hover once the scroll settles, so a
           card left under a resting cursor still shows image 2, exactly as it does today. */
        _ehPend = function () { if (imagesLink.matches(':hover')) showImg(1); };
        return;
      }
      showImg(1);
    });
    imagesLink.addEventListener('mouseleave', function () { showImg(0); });

    /* ── Mobile: horizontal swipe (only on non-carousel grids) ── */
    if (!isCarousel) {
      var touchStartX = 0, touchStartY = 0, isHorizontal = null;

      imagesLink.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isHorizontal = null;
      }, { passive: true });

      imagesLink.addEventListener('touchmove', function (e) {
        if (isHorizontal !== null) return;
        var dx = Math.abs(e.touches[0].clientX - touchStartX);
        var dy = Math.abs(e.touches[0].clientY - touchStartY);
        if (dx > 8 || dy > 8) isHorizontal = dx > dy;
      }, { passive: true });

      imagesLink.addEventListener('touchend', function (e) {
        if (isHorizontal !== true) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) < 20) return;
        showImg(dx < 0 ? 1 : 0);
      }, { passive: true });
    }
  }

  /* ── Process unprocessed summary elements lazily ──
     processCard's cloneNode + overlay setup forces synchronous layout (~63ms
     per LoAF), and the related / recently-viewed grids are below the fold, so
     we defer each card until it nears the viewport to keep that reflow out of
     the load window. IO fires immediately for any card already in view, so
     above-the-fold grids (collection, home) are unchanged. */
  var _ehIO = null;
  function ensureEhIO() {
    if (_ehIO || typeof IntersectionObserver === 'undefined') return;
    _ehIO = new IntersectionObserver(function (entries) {
      var did = false;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        _ehIO.unobserve(entry.target);
        /* ⚑ 2026-09-06: the target may be a CAROUSEL, not a card. See processAll(). */
        if (entry.target.classList && entry.target.classList.contains('eh-variant-summary')) {
          processCard(entry.target);
        } else {
          entry.target.querySelectorAll('.eh-variant-summary:not([data-eh-processed])')
            .forEach(processCard);
        }
        did = true;
      });
      if (did) document.dispatchEvent(new CustomEvent('eh:cards:processed'));
    /* ⛔ 2026-09-06: '400px 1200px' WAS TRIED HERE AND IT DOES NOTHING. A bigger
       horizontal margin looks like the fix for a carousel and is not: IntersectionObserver
       computes the intersection THROUGH clipping ancestors, and the carousel clips its own
       off-screen cards with overflow, so their intersection rect is empty however far the
       root margin reaches. Measured, unchanged, before and after. The real fix is in
       processAll() below: observe the CAROUSEL, not the cards inside it. */
    }, { rootMargin: '400px 0px' });
  }

  function processAll() {
    var cards = document.querySelectorAll('.eh-variant-summary:not([data-eh-processed])');
    if (typeof IntersectionObserver === 'undefined') {
      cards.forEach(processCard);
      return;
    }
    ensureEhIO();
    cards.forEach(function (c) {
      /* ⚑⚑ 2026-09-06: A CARD INSIDE A CAROUSEL IS OBSERVED THROUGH ITS CAROUSEL.
         Nils, 2026-09-06: "the swipe on those recommended products is a little slow so the
         next image is showing with the first image for a second. The last product in the
         last row would not remove the first."

         Cause: the carousel clips its off-screen cards, so IntersectionObserver reports
         them as not intersecting and they are never prepared. A card therefore arrived
         swiped-in in its RAW state, with Pipeline's stacked image wrappers still in place,
         which is the two-images-at-once he saw, and the last card, never fully revealed,
         stayed that way. Measured at 400px after scrolling the row into view: cards 0 and 1
         had 1 wrapper and an overlay, cards 2 to 11 had 3 wrappers and no overlay, and were
         still like that 2.3s after a swipe.

         ⚑ THE CAROUSEL, NOT THE CARDS, AND NOT ALL CARDS EAGERLY. Processing every card up
         front would build 12 flip overlays and download 12 extra images on a PDP, which is
         exactly the reflow and byte cost the lazy pass above exists to avoid. Observing the
         container keeps the laziness (nothing happens until the row is scrolled to) and
         prepares the whole row in one go, so a swipe never reveals an unprepared card. */
      var car = c.closest && c.closest('flickity-carousel, .flickity-enabled, [data-carousel]');
      if (car) {
        if (!car.__ehCarouselObserved) {
          car.__ehCarouselObserved = true;
          _ehIO.observe(car);
        }
        return;
      }
      _ehIO.observe(c);
    });
  }

  /* ── 1. Process on DOMContentLoaded (homepage, collection pages) ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAll);
  } else {
    processAll();
  }

  /* ── 2. MutationObserver for AJAX-injected sections (product recs, etc.) ── */
  if (typeof MutationObserver !== 'undefined') {
    var globalObserver = new MutationObserver(function (mutations) {
      var found = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('eh-variant-summary') && !node.dataset.ehProcessed) {
            found = true;
            break;
          }
          if (node.querySelector && node.querySelector('.eh-variant-summary:not([data-eh-processed])')) {
            found = true;
            break;
          }
        }
        if (found) break;
      }
      if (found) {
        /* Small delay to let Flickity/Pipeline finish DOM setup */
        setTimeout(processAll, 100);
      }
    });
    globalObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ── 3. Shopify section events (belt and suspenders) ── */
  document.addEventListener('shopify:section:load', function () {
    setTimeout(processAll, 200);
  });

  /* ── 4. Also handle Shopify's product recommendations loaded event ── */
  document.addEventListener('recommendations:loaded', function () {
    setTimeout(processAll, 200);
  });

  /* INT 2026-11-02 OWN WISHLIST: the v6.11 Swym delegation that sat here is gone. Every card heart
     (.collection-swym-heart / .sa-wl-heart, cloned or not) is now saved by ONE delegated handler in
     assets/int-header-wishlist.js (window.__intWl), which also paints saved hearts as cards arrive,
     including after the eh:cards:processed event this file fires. */

})();
